import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getDatabase } from "firebase/database";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getMessaging, isSupported, type Messaging } from "firebase/messaging";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const database = getDatabase(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

let messagingPromise: Promise<Messaging | null> | null = null;

/**
 * FCM messaging, or null where web push cannot work: unsupported browsers
 * (iOS Safari outside an installed PWA, some incognito modes) and insecure
 * contexts. Lazy so the messaging bundle never loads for unsupported clients.
 */
export const getMessagingIfSupported = (): Promise<Messaging | null> => {
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((supported) =>
        supported && window.isSecureContext ? getMessaging(app) : null,
      )
      .catch((error) => {
        // An unsupported browser resolves to null above; reaching this catch
        // means init actually failed — that must not masquerade as
        // "unsupported". Memoized, so it reports once per page session.
        new CoreError({
          cause: CoreErrorCause.PushNotificationsRequestFailure,
          context: "Failed to initialize Firebase messaging",
          underlyingException: error,
        });
        return null;
      });
  }
  return messagingPromise;
};

export { firebaseConfig };
export default app;
